import React from "react";
import { motion } from "framer-motion";
import {
  FaResearchgate,
  FaSketch,
  FaProjectDiagram,
  FaRulerCombined,
  FaPalette,
  FaPencilRuler,
  FaCheckCircle,
  FaUserCheck,
  FaLightbulb,
  // Icons for new sections
  FaUserTie,
  FaChartLine,
  FaHeadset,
  FaDesktop,
  FaFlask,
  FaCode,
} from "react-icons/fa";
import "./UIUXDevelopment.scss"; 
import ServicesCommon from "../ServicesCommon/ServicesCommon";
import { useNavigate } from "react-router-dom";
import UIUXImg from "../../../assets/images/uiux.png";
// --- Framer Motion Variants ---

const sectionFade = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.17, 0.55, 0.55, 1] },
  },
};

const listContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const UIUXDevelopment = () => {
  const navigate = useNavigate();

  // Data for CORE SERVICES CHECKLIST (from image_d63827.png)
  const coreServicesChecklist = [
    { icon: FaResearchgate, name: "Research" },
    { icon: FaSketch, name: "Storyboarding" },
    { icon: FaProjectDiagram, name: "Hybrid Model" },
    { icon: FaRulerCombined, name: "Wireframes" },
    { icon: FaPalette, name: "Visual Design" },
    { icon: FaPencilRuler, name: "Graphic Design" },
    { icon: FaLightbulb, name: "Branding" },
    { icon: FaUserCheck, name: "User Testing" },
  ];

  // Data for Core Deliverables section
  const deliverables = [
    { 
        icon: FaRulerCombined, 
        title: "Wireframes & Prototypes", 
        detail: "Interactive, low-fidelity models to map out user flow and screen structure before coding begins." 
    },
    { 
        icon: FaPalette, 
        title: "Style Guides & UI Kits", 
        detail: "Documented guidelines covering typography, color palettes, components, and design system." 
    },
    { 
        icon: FaCheckCircle, 
        title: "Usability Reports", 
        detail: "Detailed findings from user testing sessions, identifying pain points and necessary improvements." 
    },
    { 
        icon: FaUserCheck, 
        title: "Persona & Journey Maps", 
        detail: "In-depth profiles of target users and visualization of their complete interaction path." 
    },
  ];



  return (
    <div className="uiux-dev-page">
      {/* ------------------ 1. HERO SECTION ------------------ */}
      <section className="hero-section-dev">
        <div className="hero-content-left">
          <motion.p
            className="hero-tag-dev"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            UI/UX DEVELOPMENT
          </motion.p>

          <motion.h1
            className="hero-title-dev"
            variants={sectionFade}
            initial="hidden"
            animate="visible"
          >
            Creative, Intuitive, Immersive{" "}
            <motion.span
              initial={{ color: "#333" }}
              animate={{ color: "#5E99EF" }} // Blue accent
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: "mirror",
              }}
            >
              UI/UX Designs
            </motion.span>
          </motion.h1>

          <motion.p
            className="hero-subtext-dev"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            We at Econsign Soft work in dedicated project teams, with creative, intuitive, immersive UI UX designs.
          </motion.p>

          <motion.p
            className="hero-desc-dev"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            At Econsign Soft, we help our clients to create brilliant designs and superior user experiences through UX research, behavioral analytics and market insights. We explore innovative digital experience with a blend of technology, creativity and personalization in UI and UX development services.
          </motion.p>

          <motion.button
            className="primary-btn-dev"
            variants={listItem}
            initial="hidden"
            animate="visible"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 30px rgba(94, 153, 239, 0.4)",
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/contact-us")}
          >
            Contact Us
          </motion.button>
        </div>

        {/* ------------------ Illustration (Image Placeholder) ------------------ */}
        <motion.div
          className="dev-hero-UIUX-illustration"
          initial={{ opacity: 0, x: 50, rotate: 5 }}
          animate={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.5, type: "spring", stiffness: 100 }}
        >
            <img
                       src={UIUXImg}
                       alt="UiUx Development Illustration"
                       className="responsive-UiUx-img"
                     />
        </motion.div>
      </section>
      
      {/* ------------------ 2. CORE UI/UX SERVICES CHECKLIST (Software Dev Style) ------------------ */}
      <section className="uiux-core-services-section">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Our <span className="highlight-core">End-to-End</span> UI/UX Development Offerings
        </motion.h2>
        
        <motion.div
          className="core-services-grid"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {coreServicesChecklist.map((service, i) => (
            <motion.div className="core-service-item" key={i} variants={listItem}>
              <div className="icon-wrapper">
                <service.icon className="core-service-icon" />
              </div>
              <p>{service.name}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>


      {/* ------------------ 3. FEATURES & BUSINESS SUCCESS SECTION ------------------ */}
      <section className="dev-features-benefits-section">
        
        {/* Top Features (from image_d69621.png) */}
        <motion.div
            className="top-benefits-grid"
            variants={listContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
        >
            <motion.div className="benefit-card" variants={listItem}>
                <FaDesktop className="benefit-icon" />
                <h3>Responsive Pixel Perfect Design</h3>
                <p>Econsig soft craft splendid and deliver experiences that shines. We help startups & businesses with creative, intuitive, immersive UI/UX designs that seamlessly guide users in performing the desired actions flawlessly. We help brands create brilliant designs and superior user experiences through UX research, behavioral analytics and market insights.</p>
            </motion.div>
            <motion.div className="benefit-card" variants={listItem}>
                <FaHeadset className="benefit-icon" />
                <h3>24/7 Smart Support</h3>
                <p>We at Econsign Soft are always available for quick smart support 24/7.</p>
            </motion.div>
        </motion.div>
        
      </section>

      {/* ------------------ 4. CORE DELIVERABLES SECTION ------------------ */}
      <section className="deliverables-section">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Key <span className="highlight-deliverable">Deliverables</span> From Our Process
        </motion.h2>

        <motion.div
          className="deliverables-grid"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {deliverables.map((item, i) => (
            <motion.div className="deliverable-card" key={i} variants={listItem}>
              <item.icon className="deliverable-icon" />
              <h3>{item.title}</h3>
              <p>{item.detail}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ------------------ 5. COMMON SECTION ------------------ */}
      <ServicesCommon />
    </div>
  );
};

export default UIUXDevelopment;